import { useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Upload, Sparkles, Link as LinkIcon, Loader2, X, Image as ImageIcon } from "lucide-react";

/**
 * Universal image picker — three modes:
 *   - Upload a local file
 *   - Generate via AI (Gemini Nano Banana) from a text prompt
 *   - Paste a URL
 *
 * Calls backend endpoints /api/uploads/image and /api/uploads/generate-image.
 * Returns the resulting public URL via onChange(url).
 *
 * Props:
 *   value     — current url (controlled)
 *   onChange  — (url: string) => void
 *   accent    — "slate" | "amber" | "rose" (matches plan family color)
 *   suggestedPrompt — optional initial AI prompt (e.g. "Diwali Grand Feast - veg biryani thali")
 */
const ACCENTS = {
  slate: { bg: "bg-slate-900",  ring: "ring-slate-300",  pill: "bg-slate-100 text-slate-700" },
  amber: { bg: "bg-amber-700",  ring: "ring-amber-300",  pill: "bg-amber-100 text-amber-700" },
  rose:  { bg: "bg-rose-700",   ring: "ring-rose-300",   pill: "bg-rose-100 text-rose-700" },
};

export default function ImagePicker({ value, onChange, accent = "slate", suggestedPrompt = "" }) {
  const [mode, setMode] = useState("ai");
  const [busy, setBusy] = useState(false);
  const [prompt, setPrompt] = useState(suggestedPrompt);
  const [urlInput, setUrlInput] = useState("");
  const fileRef = useRef(null);
  const acc = ACCENTS[accent];

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) return toast.error("File must be under 8 MB");
    const fd = new FormData();
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/uploads/image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      onChange(data.url);
      toast.success("Image uploaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  const handleGenerate = async () => {
    if (!prompt.trim()) return toast.error("Describe the dish first");
    setBusy(true);
    try {
      const { data } = await api.post("/uploads/generate-image", { prompt: prompt.trim() });
      onChange(data.url);
      toast.success("Image generated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const handleUrl = () => {
    if (!urlInput.trim()) return;
    onChange(urlInput.trim());
    setUrlInput("");
    toast.success("Image URL set");
  };

  return (
    <div data-testid="image-picker">
      {/* Preview row */}
      <div className="flex gap-3 items-start mb-3">
        <div className={`w-28 h-28 rounded-2xl bg-cream-200 overflow-hidden flex items-center justify-center ring-2 ${acc.ring}`}>
          {value ? (
            <img src={value} alt="" className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = "none"; }} />
          ) : (
            <ImageIcon size={28} className="text-ink-400" />
          )}
        </div>
        <div className="flex-1">
          <div className="text-xs font-bold uppercase tracking-wider text-ink-500">Cover image</div>
          <div className="text-[11px] text-ink-500 mt-1 break-all max-h-12 overflow-hidden">
            {value ? value : "No image set."}
          </div>
          {value && (
            <button data-testid="img-remove" type="button" onClick={() => onChange("")}
              className="mt-2 text-[10px] inline-flex items-center gap-1 font-bold uppercase text-rose-600 hover:text-rose-700">
              <X size={11} /> Remove
            </button>
          )}
        </div>
      </div>

      {/* Mode tabs */}
      <div className="inline-flex bg-cream-200 rounded-full p-1 mb-3">
        {[
          { key: "ai",     label: "AI generate", Icon: Sparkles },
          { key: "upload", label: "Upload",      Icon: Upload },
          { key: "url",    label: "URL",         Icon: LinkIcon },
        ].map(t => (
          <button key={t.key} type="button" data-testid={`img-mode-${t.key}`}
            onClick={() => setMode(t.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold flex items-center gap-1.5 ${
              mode === t.key ? `${acc.bg} text-white` : "text-ink-700 hover:bg-cream-300"
            }`}>
            <t.Icon size={12} /> {t.label}
          </button>
        ))}
      </div>

      {/* AI generate */}
      {mode === "ai" && (
        <div className="space-y-2">
          <textarea data-testid="img-prompt" rows={2} value={prompt}
            onChange={e => setPrompt(e.target.value)}
            placeholder="e.g. masala dosa with coconut chutney and sambar on a banana leaf"
            className="block w-full rounded-xl border border-border px-3 py-2 text-sm" />
          <button type="button" data-testid="img-generate" disabled={busy || !prompt.trim()} onClick={handleGenerate}
            className={`w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white ${acc.bg} hover:opacity-90 disabled:opacity-40`}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
            {busy ? "Generating…" : "Generate with AI"}
          </button>
          <div className="text-[10px] text-ink-500">Takes ~10-20s. Powered by Gemini Nano Banana.</div>
        </div>
      )}

      {/* Upload */}
      {mode === "upload" && (
        <div className="space-y-2">
          <input ref={fileRef} type="file" accept="image/*" className="hidden"
            data-testid="img-file-input"
            onChange={e => { handleFile(e.target.files?.[0]); e.target.value = ""; }} />
          <button type="button" data-testid="img-upload-btn" disabled={busy}
            onClick={() => fileRef.current?.click()}
            className={`w-full inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold text-white ${acc.bg} hover:opacity-90 disabled:opacity-40`}>
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
            {busy ? "Uploading…" : "Choose a file (max 8 MB)"}
          </button>
          <div className="text-[10px] text-ink-500">PNG / JPG / WebP supported.</div>
        </div>
      )}

      {/* URL */}
      {mode === "url" && (
        <div className="space-y-2">
          <div className="flex gap-2">
            <input data-testid="img-url-input" type="url" value={urlInput}
              onChange={e => setUrlInput(e.target.value)}
              placeholder="https://example.com/dish.jpg"
              className="flex-1 rounded-xl border border-border px-3 py-2 text-sm" />
            <button type="button" data-testid="img-url-set" onClick={handleUrl}
              disabled={!urlInput.trim()}
              className={`px-4 py-2 rounded-full text-sm font-semibold text-white ${acc.bg} hover:opacity-90 disabled:opacity-40`}>
              Use
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
